import { useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';

import { toCamelCase, toSnakeCase } from '@/lib/case';
import { useAuthAxios } from '@/hooks/useAuthAxios';
import { invalidateGtProgress } from '@/features/GroundTruthHub/api';
import { getServerErrorMessage, isConflictError } from '@/features/GroundTruthHub/errors';
import { LeasingDocs } from '@/models/docs';

import { useHeaderSaveVersion } from './headerSaveVersion';

export interface SearchDocumentsResponse {
    total: number;
    results: LeasingDocs[];
}

export interface DocTypeOption {
    value: string;
    label: string;
}

export type SortOption = 'newest' | 'oldest' | 'updated';

export const useSearchDocuments = ({
    q,
    docType,
    sort = 'newest',
    limit = 50,
    skip = 0,
    enabled = true,
}: {
    q?: string | null;
    docType?: string | null;
    sort?: SortOption;
    limit?: number;
    skip?: number;
    enabled?: boolean;
}) => {
    const { client, enabled: authEnabled } = useAuthAxios();

    return useQuery({
        queryKey: ['documentSearch', { q, docType, sort, limit, skip }],
        queryFn: async () => {
            const response = await client.get(
                `/api/leasing/documents/search`,
                {
                    params: {
                        q: q || undefined,
                        doc_type: docType || undefined,
                        sort: sort || undefined,
                        limit,
                        skip,
                    },
                },
            );
            return toCamelCase<SearchDocumentsResponse>(response.data);
        },
        enabled: authEnabled && enabled,
    });
};

export const useDocumentTypes = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['documentTypes'],
        queryFn: async () => {
            const response = await client.get(
                `/api/leasing/documents/types`,
            );
            return response.data as DocTypeOption[];
        },
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/** Document-type codes that have a Ground Truth header form (the backend registry's keys). */
export const useGtHeaderDocTypes = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['gtHeaderDocTypes'],
        queryFn: async () => {
            const response = await client.get(
                `/api/ground-truth/header-document-types`,
            );
            return response.data as string[];
        },
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const useGetLeasingDocument = (docId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['leasingDocument', docId],
        queryFn: () => fetchLeasingDocument(client, docId as number),
        enabled: enabled && docId !== null,
    });
};

export async function fetchLeasingDocument(client: AxiosInstance, docId: number): Promise<LeasingDocs> {
    const response = await client.get(`/api/leasing/${docId}`);
    return toCamelCase<LeasingDocs>(response.data);
}

/**
 * Header save. Inside a `HeaderSaveGuard` it sends the version the form was loaded from,
 * and a 409 (someone else saved first) goes to the guard's Reload notice, not `onError`.
 */
export const useUpdateGroundTruth = (docId: number) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();
    const version = useHeaderSaveVersion();

    const mutation = useMutation({
        mutationFn: async (data: {
            documentType: string;
            document: Record<string, unknown>;
        }) => {
            const response = await client.put(
                `/api/ground-truth/docs/${docId}`,
                toSnakeCase(data),
                { params: { base_version: version?.baseVersion.current } },
            );
            return toCamelCase<LeasingDocs>(response.data);
        },
        onSuccess: (saved) => {
            if (version) version.baseVersion.current = saved.headerVersion;
            queryClient.invalidateQueries({ queryKey: ['documentSearch'] });
            queryClient.invalidateQueries({ queryKey: ['leasingDocument', docId] });
            invalidateGtProgress(queryClient);
        },
    });

    const { mutate } = mutation;
    const mutateGuarded: typeof mutate = useCallback(
        (variables, options) =>
            mutate(variables, {
                ...options,
                onError: (error, ...rest) =>
                    version && isConflictError(error)
                        ? version.onConflict(getServerErrorMessage(error, 'ground truth'))
                        : options?.onError?.(error, ...rest),
            }),
        [mutate, version],
    );

    return { ...mutation, mutate: mutateGuarded };
};

// Cached preview URLs stay fresh for a minute so remounts (Back, tab switch) skip a
// refetch; well inside the backend's one-hour presign expiry.
const PREVIEW_STALE_TIME_MS = 60_000;

export const useDocumentPreviewUrl = (docId: number | null) => {
    const { client, enabled } = useAuthAxios();

    const query = useQuery({
        queryKey: ['docPreviewUrl', docId],
        queryFn: async () => {
            const response = await client.get<{ url: string }>(`/api/docs/${docId}/preview_url`);
            return response.data.url;
        },
        enabled: enabled && docId !== null,
        staleTime: PREVIEW_STALE_TIME_MS,
    });

    return {
        url: query.data ?? null,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch,
        isRefetching: query.isRefetching,
    };
};
